/**
 * Logique pure du sélecteur avec recherche (`components/SearchSelect.vue`, lot N) : en production, 340 clients
 * Axonaut… un `<select>` natif n'est plus utilisable. La recherche ignore la casse et les
 * accents, trouve le texte n'importe où dans le libellé ou dans le texte secondaire (« Axonaut 13340495 »), et
 * accepte plusieurs mots dans le désordre (« coutant menuis »).
 */

export interface SearchOption {
  value: string;
  label: string;
  /** Texte secondaire affiché en petit et cherchable (identifiant Axonaut, date, statut…). */
  secondary?: string | null;
  disabled?: boolean;
}

/** Nombre d'options affichées au plus (au-delà : « Tape pour affiner »). */
export const SEARCH_SELECT_MAX_VISIBLE = 200;

/** Minuscules, sans accents ni ligatures, apostrophes et espaces unifiés : « Kiné  Océane » → « kine oceane ». */
export function normalizeSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('fr')
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/[’‘`´]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** Tous les mots de la recherche figurent dans le libellé ou le texte secondaire. */
export function matchesOption(option: SearchOption, query: string): boolean {
  const words = normalizeSearch(query).split(' ').filter(Boolean);
  if (words.length === 0) return true;
  const haystack = normalizeSearch(`${option.label} ${option.secondary ?? ''}`);
  return words.every((w) => haystack.includes(w));
}

/**
 * Options correspondant à la recherche : celles dont le libellé COMMENCE par la recherche d'abord, puis les
 * autres, chacune dans l'ordre d'origine. Recherche vide : toutes les options.
 */
export function filterOptions<T extends SearchOption>(options: readonly T[], query: string): T[] {
  const q = normalizeSearch(query);
  if (!q) return [...options];
  const prefix: T[] = [];
  const rest: T[] = [];
  for (const option of options) {
    if (!matchesOption(option, q)) continue;
    (normalizeSearch(option.label).startsWith(q) ? prefix : rest).push(option);
  }
  return [...prefix, ...rest];
}

/**
 * Option active suivante au clavier (↑ = -1, ↓ = +1), en sautant les options désactivées et en bouclant.
 * -1 si aucune option n'est choisissable.
 */
export function nextActiveIndex(options: readonly SearchOption[], current: number, delta: 1 | -1): number {
  const n = options.length;
  if (n === 0) return -1;
  let index = current < 0 ? (delta === 1 ? -1 : n) : current;
  for (let i = 0; i < n; i += 1) {
    index = (index + delta + n) % n;
    if (!options[index]?.disabled) return index;
  }
  return -1;
}
